import {
    SEMRESATTRS_SERVICE_NAME,
    SEMRESATTRS_SERVICE_VERSION,
    SEMRESATTRS_HOST_NAME,
} from '@opentelemetry/semantic-conventions';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { Resource } from '@opentelemetry/resources';
import { EzloOTLPTraceExporter } from './services/TraceExporter';
import packageJson from '../../../package.json';
import { WEBUI_VERSION } from 'constants/App';
import { OPEN_TELEMETRY_TRACES_URL } from './constants/api';
import { trace } from '@opentelemetry/api';

/**
 * Initializes and configures the OpenTelemetry WebTracerProvider for the application.
 *
 * - Sets up service/resource attributes (service name, version, host).
 * - Registers a custom OTLP trace exporter with batching and keepalive.
 * - Uses ZoneContextManager for context propagation.
 * - Optionally adds document load instrumentation (except in test env).
 *
 * @returns {import('@opentelemetry/api').Tracer} The initialized OpenTelemetry tracer instance for the app.
 *
 * @example
 * import { tracer } from './path/to/initOtlTracer';
 * const span = tracer.startSpan('my-operation');
 * span.end();
 */
function initOtlTracer() {
    const provider = new WebTracerProvider({
        resource: Resource.default().merge(
            new Resource({
                [SEMRESATTRS_SERVICE_NAME]: packageJson.name,
                [SEMRESATTRS_SERVICE_VERSION]: WEBUI_VERSION,
                [SEMRESATTRS_HOST_NAME]: window?.location?.hostname || 'unknown',
            }),
        ),
    });

    const exporterOptions = {
        url: OPEN_TELEMETRY_TRACES_URL,
        headers: {},
        keepAlive: true,
    };

    const traceExporter = new EzloOTLPTraceExporter(exporterOptions);

    provider.addSpanProcessor(new BatchSpanProcessor(traceExporter));

    provider.register({
        contextManager: new ZoneContextManager(),
    });

    const getInstrumentations = () => {
        if (process.env.NODE_ENV === 'test') {
            return [];
        }

        return [new DocumentLoadInstrumentation()];
    };

    // Registering instrumentations
    registerInstrumentations({
        instrumentations: getInstrumentations(),
        tracerProvider: provider,
    });

    return trace.getTracer(packageJson.name);
}

/**
 * The shared OpenTelemetry tracer instance for the application.
 * @type {import('@opentelemetry/api').Tracer}
 */
export const tracer = initOtlTracer();
